import { capitalize } from '../string/lib';
import dayjs from 'dayjs';

import {
	DynamicQueryOperator,
	DynamicQueryParams,
	SonarV3SortDirection,
	SortByOption
} from '@sonar-software/sonar-ts-types';

const nonSequentialDoubleQuotes = /(?<!")"(?!")/gu;

export type ParsedFilterEntry = {
	operator: DynamicQueryOperator;
	value: string;
};

export type ParsedFilterLookup = Record<string, Array<ParsedFilterEntry>>;

export function splitDynamicFilterString(filterString: string | undefined): Array<string> {
	const results: Array<string> = [];

	if (!filterString) {
		return results;
	}

	return filterString.split(/ +(?=(?:(?:[^"]*"){2})*[^"]*$)/gu);
}

export function buildDynamicFilterLookupFromString(filterString: string | undefined): ParsedFilterLookup {
	return splitDynamicFilterString(filterString).reduce((prev, current) => {
		const [ key, operator, ...rest ] = current.split('~');

		const entry: ParsedFilterEntry = {
			operator: operator as DynamicQueryOperator,
			value: rest.join('~').replace(nonSequentialDoubleQuotes, '')
				.replace(/%/gu, '')
		};

		if (!prev[ key ]) {
			prev[ key ] = [];
		}

		prev[ key ].push(entry);

		return prev;
	}, {} as ParsedFilterLookup);
}

export function getValuesFromDynamicFilterLookup(filter: string, lookup: ParsedFilterLookup): Array<ParsedFilterEntry> {
	return lookup[ filter ] ?? [];
}

export function getValuesFromDynamicFilterString(filter: string, filterString: string): Array<ParsedFilterEntry> {
	return getValuesFromDynamicFilterLookup(filter, buildDynamicFilterLookupFromString(filterString));
}

export function getRangeValuesFromDynamicFilterLookup(
	filter: string,
	lookup: ParsedFilterLookup
): [number | null, number | null] {
	const results: [number | null, number | null] = [ null, null ];
	const entries = getValuesFromDynamicFilterLookup(filter, lookup);

	if (!entries.length) {
		return results;
	}

	entries.forEach((entry) => {
		if (entry.operator === DynamicQueryOperator.GreaterThanOrEqualTo) {
			results[ 0 ] = Number(entry.value);
		} else {
			results[ 1 ] = Number(entry.value);
		}
	});

	return results;
}

export function getDateRangeValuesFromDynamicFilterLookup(
	filter: string,
	lookup: ParsedFilterLookup
): [dayjs.Dayjs | null, dayjs.Dayjs | null] {
	const results: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [ null, null ];
	const entries = getValuesFromDynamicFilterLookup(filter, lookup);

	if (!entries.length) {
		return results;
	}

	entries.forEach((entry) => {
		if (entry.operator === DynamicQueryOperator.GreaterThanOrEqualTo) {
			results[ 0 ] = dayjs(entry.value);
		} else {
			results[ 1 ] = dayjs(entry.value);
		}
	});

	return results;
}

export function getStatusValueFromDynamicFilterLookup(filter: string, lookup: ParsedFilterLookup): number | null {
	const value = getValuesFromDynamicFilterLookup(filter, lookup)[ 0 ]?.value;

	if (!value) {
		return null;
	}

	return Number(value);
}

export function getMultiSelectValuesFromDynamicFilterLookup(
	filter: string,
	lookup: ParsedFilterLookup,
	useNullString?: boolean
): Array<string | number | boolean> {
	const valueEntries = getValuesFromDynamicFilterLookup(filter, lookup);

	if (!valueEntries.length) {
		return [];
	}

	const [ { operator, value } ] = valueEntries;

	switch (operator) {
	case DynamicQueryOperator.BetweenWithNull:
	case DynamicQueryOperator.Between:
	case DynamicQueryOperator.Inn:
	case DynamicQueryOperator.In:
	case DynamicQueryOperator.NotIn:
		const values: Array<string | number | boolean> = [];

		if (operator === DynamicQueryOperator.Inn) {
			values.push(useNullString ? 'null' : -1);
		}

		values.push(...value.split('~').map(
			(id) => (isNaN(Number(id)) ? id.replaceAll(nonSequentialDoubleQuotes, '') : Number(id))
		));

		if (operator === DynamicQueryOperator.Between) {
			values.push(true);
		}

		return values;
	case DynamicQueryOperator.IsNull:
		return useNullString ? [ 'null' ] : [ -1 ];
	default:
		return [];
	}
}

export function getOrderingFromDynamicOrderString(
	orderString: string,
	sortOptions: Array<SortByOption>
): SortByOption | null {
	if (!orderString || !sortOptions.length) {
		return null;
	}

	const [ sortBy, sortDirection ] = orderString.split('~');

	const targetOption = sortOptions.find((option) => option.key === sortBy);

	if (targetOption) {
		const direction = sortDirection === 'a' ? SonarV3SortDirection.Ascending : SonarV3SortDirection.Descending;

		return {
			...targetOption,
			direction
		};
	}

	return null;
}

function shouldAddQuotes(value: number | string | null): boolean {
	return value !== null && isNaN(Number(value));
}

export function makeDynamicFilterString(
	key: string,
	value: number | string | null | Array<string> | Array<number> | Array<string | number>,
	operator: DynamicQueryOperator
): string {
	switch (operator) {
	case DynamicQueryOperator.Between:
	case DynamicQueryOperator.BetweenWithNull:
	case DynamicQueryOperator.BitwiseOr:
	case DynamicQueryOperator.BitwiseOrWithNull:
	case DynamicQueryOperator.Equals:
	case DynamicQueryOperator.GreaterThan:
	case DynamicQueryOperator.GreaterThanOrEqualTo:
	case DynamicQueryOperator.In:
	case DynamicQueryOperator.Inn:
	case DynamicQueryOperator.LessThan:
	case DynamicQueryOperator.LessThanOrEqualTo:
	case DynamicQueryOperator.NotEqual:
	case DynamicQueryOperator.NotIn:
		let parsedValue = '';

		if (Array.isArray(value)) {
			parsedValue = value.map((part) => (shouldAddQuotes(part) ? `"${part}"` : part)).join('~');
		} else {
			parsedValue = shouldAddQuotes(value) ? `"${value}"` : String(value);
		}

		return parsedValue ? `${key}~${operator}~${parsedValue}` : '';
	case DynamicQueryOperator.IsNull:
	case DynamicQueryOperator.NotNull:
		return `${key}~${operator}`;
	case DynamicQueryOperator.Like:
	case DynamicQueryOperator.NotLike:
		return value ? `${key}~${operator}~"%${value}%"` : '';
	default:
		return '';
	}
}

export function makeDynamicFilterBetweenString(
	key: string,
	value: [number | undefined, number | undefined, boolean | undefined]
): string {
	const [ start, end, excludeNulls ] = value;

	if (isNaN(Number(start)) || isNaN(Number(end))) {
		return '';
	}

	const operator = excludeNulls ? DynamicQueryOperator.Between : DynamicQueryOperator.BetweenWithNull;

	return makeDynamicFilterString(key, [ Number(start), Number(end) ], operator);
}

export function makeDynamicFilterRangeString(key: string, value: Array<number | string | undefined>): string {
	const [ start, end ] = value;

	const rangeArray = [];

	if (start) {
		rangeArray.push(makeDynamicFilterString(key, start, DynamicQueryOperator.GreaterThanOrEqualTo));
	}

	if (end) {
		rangeArray.push(makeDynamicFilterString(key, end, DynamicQueryOperator.LessThanOrEqualTo));
	}

	return rangeArray.join(' ');
}

export type DynamicQueryLanguageFilterParam<T, P> = {
	name?: string;
	operator?: DynamicQueryOperator;
	rangeEnd?: keyof P;
	value?: (value: T) => unknown;
};

export type DynamicQueryLanguageFilterParams<P> = Partial<{
	[K in keyof P]: boolean | DynamicQueryLanguageFilterParam<P[K], P>
}>;

export type DynamicQueryLanguageParams<P> = {
	capitalizeFilterParams?: boolean;
	capitalizeOrderingParams?: boolean;
	defaultOperator?: DynamicQueryOperator;
	filterParams?: DynamicQueryLanguageFilterParams<P>;
	orderingParams?: Record<string, string>;
	removeParams?: Array<keyof P>;
};

export function queryParamsToDynamicQueryLanguageParams<
	P extends Record<string, unknown>,
	D extends DynamicQueryLanguageParams<P>
>(
	queryParams: P,
	options?: D
): Omit<P, 'sortBy' | 'sortDirection'> & DynamicQueryParams {
	let filters = '';
	const { sortBy, sortDirection, ...rest } = queryParams;
	const filtersCapitalize = options?.capitalizeFilterParams ?? true ? capitalize : (param: string) => param;
	const orderingCapitalize = options?.capitalizeOrderingParams ?? true ? capitalize : (param: string) => param;

	Object.entries(options?.filterParams ?? {}).forEach(([ defaultKey, filterOptions ]) => {
		const defaultValue = queryParams[ defaultKey ];
		let value = filterOptions.value ? filterOptions.value(defaultValue) : defaultValue;
		delete rest[ defaultKey ];

		if (filterOptions.rangeEnd) {
			delete rest[ filterOptions.rangeEnd ];
		}

		if (typeof value === 'undefined') {
			return;
		}

		const key = filtersCapitalize(filterOptions.name ?? defaultKey);
		let operator = filterOptions.operator ?? options?.defaultOperator ?? DynamicQueryOperator.Equals;

		if (Array.isArray(value)) {
			const values = value as Array<string | number>;

			if (values.includes('null') || values.includes(-1)) {
				operator = values.length === 1 ? DynamicQueryOperator.IsNull : DynamicQueryOperator.Inn;
				value = values.filter((x) => x !== -1 && x !== 'null');
			} else {
				operator = DynamicQueryOperator.In;
			}
		}

		const newFilter = filterOptions.rangeEnd ?
			makeDynamicFilterRangeString(key, [ value, queryParams[ filterOptions.rangeEnd ] ]) :
			makeDynamicFilterString(key, value, operator);

		const space = newFilter && filters ? ' ' : '';
		filters = `${filters}${space}${newFilter}`;
	});

	if (options?.removeParams) {
		options.removeParams.forEach((param) => {
			delete rest[ param as string ];
		});
	}

	const direction = sortDirection === SonarV3SortDirection.Ascending ? 'a' : 'd';
	const orderingString = options?.orderingParams?.[ String(sortBy) ] ?? sortBy;
	const ordering = sortBy && Number.isInteger(sortDirection) ?
		`${orderingCapitalize(String(orderingString))}~${direction}` : '';

	return {
		...rest,
		filters,
		ordering
	};
}
